import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import img1 from "../../assets/homeAssets/bannerImg2.png";
import img2 from "../../assets/homeAssets/bannerImg3.jpg";
import img3 from "../../assets/homeAssets/bannerImg1.png";

// --- Animations ---
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const textGlow = keyframes`
  0%, 100% { text-shadow: 0 0 10px rgba(255, 213, 158, 0.3); }
  50% { text-shadow: 0 0 20px rgba(255, 210, 158, 0.5); }
`;

const buttonEntrance = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(40px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

// --- Styled Components ---
const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  color: #5a472d;
  position: relative;
  overflow: hidden;
  padding: 10% 8%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(
        circle at 50% 30%,
        rgba(255, 255, 255, 0.4) 0%,
        transparent 30%
      ),
      radial-gradient(
        circle at 50% 70%,
        rgba(255, 255, 255, 0.3) 0%,
        transparent 30%
      );
    z-index: 1;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  @media (max-width: 650px) {
    margin-top: 5em;
  }
`;

const DesktopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 10px;
  font-size: clamp(2.8rem, 7.5vw, 6rem);
  line-height: 1.1;
  position: relative;
  z-index: 2;
  animation: ${fadeInUp} 0.8s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  opacity: 0;

  @media (max-width: 1000px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const MobileColumn = styled.div`
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 0px;

  @media (max-width: 600px) {
    display: flex;
  }
`;

const MobileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(2.2rem, 8vw, 3.5rem);
  line-height: 1.2;
  position: relative;
  z-index: 2;
  animation: ${fadeInUp} 0.8s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  opacity: 0;
  width: 100%;
  text-align: center;

  @media (max-width: 600px) {
    margin-bottom: 5px;
  }
`;

const MobileImageRow = styled(MobileRow)`
  margin: 10px 0;
`;

const Text = styled.span`
  font-weight: 300;
  letter-spacing: -0.02em;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  padding: 8px 12px;
  border-radius: 12px;
  display: inline-block;
  font-size: 6vw;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(212, 157, 74, 0.2);
  }

  &.emphasis {
    font-weight: 700;
    color: #7e6f5b;
    position: relative;
    animation: ${textGlow} 3s ease-in-out infinite;

    &::after {
      content: "";
      position: absolute;
      bottom: 5px;
      left: 12%;
      width: 76%;
      height: 4px;
      background: linear-gradient(90deg, transparent, #c9b397, transparent);
      border-radius: 2px;
      opacity: 0.7;
    }
  }
  @media (max-width: 1600px) {
    font-size: 5vw;
  }
  @media (max-width: 1000px) {
    font-size: 9vw;
  }
  @media (max-width: 600px) {
    font-size: 13vw;
    line-height: 0.4;
    padding: 0px 8px;
  }
`;

const ImageWindow = styled.div`
  background-image: url(${(props) => props.src});
  background-size: cover;
  background-position: center;
  height: 1.5em;
  width: ${(props) => (props.type === "circle" ? "1em" : "3.5em")};
  border-radius: ${(props) => (props.type === "circle" ? "50%" : "500px")};
  position: relative;
  z-index: 2;

  /* Animation only applies above 600px */
  @media (min-width: 601px) {
    animation: ${floatAnimation} 6s ease-in-out infinite;
    animation-delay: ${(props) => props.delay || "0s"};
  }

  box-shadow:
    0 15px 35px rgba(212, 168, 74, 0.25),
    0 8px 20px rgba(255, 158, 192, 0.2),
    inset 0 0 0 3px rgba(255, 255, 255, 0.9);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;

  &:hover {
    transform: scale(1.15) rotate(8deg);
    box-shadow:
      0 20px 45px rgba(212, 148, 74, 0.4),
      0 12px 30px rgb(201, 199, 188),
      inset 0 0 0 3px rgba(255, 255, 255, 1);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );

    /* Shimmer animation only applies above 600px */
    @media (min-width: 601px) {
      animation: ${shimmerAnimation} 4s infinite;
      animation-delay: ${(props) => props.delay || "0s"};
    }

    z-index: 2;
  }

  @media (max-width: 1600px) {
    height: 1.2em;
  }

  @media (max-width: 600px) {
    height: 2.2em;
    width: ${(props) => (props.type === "circle" ? "1.8em" : "5em")};
    animation: none !important;
    transform: none !important;
    margin: 5px 0;

    &::after {
      animation: none !important;
    }
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  max-width: 650px;
  text-align: center;
  margin: 20px auto 0;
  line-height: 1.7;
  color: #7a6d4a;
  position: relative;
  z-index: 2;
  animation: ${fadeInUp} 1s ease-out forwards;
  animation-delay: 1.2s;
  opacity: 0;
  font-weight: 300;
  padding: 0 20px;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 1600px) {
    font-size: 20px;
  }
  @media (max-width: 1000px) {
    font-size: 1rem;
  }

  @media (max-width: 600px) {
    font-size: 14px;
    margin-top: 20px;
    padding: 0 15px;
  }
`;

const CTAButton = styled.button`
  margin-top: 30px;
  padding: 20px 50px;
  background: linear-gradient(135deg, #917d6f, #6b6156);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  position: relative;
  z-index: 3;
  animation: ${(props) => (props.shouldAnimate ? buttonEntrance : "none")} 0.8s
    ease-out forwards;
  animation-delay: ${(props) => (props.shouldAnimate ? "1.5s" : "2s")};
  opacity: ${(props) => (props.shouldAnimate ? 0 : 1)};
  transform: ${(props) =>
    props.shouldAnimate
      ? "translateY(40px) scale(0.95)"
      : "translateY(0) scale(1)"};
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(212, 168, 74, 0.4);
  transform-origin: center;
  font-family: "Montserrat";

  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: 0 25px 50px rgba(145, 125, 111, 0.6);
  }

  &:active {
    transform: translateY(-4px) scale(1.02);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.7s;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 600px) {
    margin-top: 20px;
    padding: 15px 40px;
    font-size: 1rem;
  }
`;

const CenterAccent = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(212, 168, 74, 0.1) 0%,
    transparent 70%
  );
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;

  @media (max-width: 768px) {
    width: 400px;
    height: 400px;
  }

  @media (max-width: 600px) {
    width: 300px;
    height: 300px;
  }
`;

// --- Component ---
const Banner = () => {
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [loaded, setLoaded] = useState(false);
  const [shouldAnimateButton, setShouldAnimateButton] = useState(true);
  const buttonRef = useRef(null);

  const handleButtonClick = () => {
    navigate("/kontakt"); // Navigate to kontakt page
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);

      setTimeout(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight;

          if (!isInViewport) {
            setShouldAnimateButton(true);
          } else {
            setShouldAnimateButton(false);
          }
        }
      }, 100);
    }, 12100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <HeroSection className={loaded ? "loaded" : ""}>
      <CenterAccent />

      <ContentContainer>
        {/* Desktop Layout */}
        <DesktopRow delay="0.2s">
          <Text className="emphasis">Discover</Text>
          <ImageWindow
            src={img1}
            type="pill"
            delay="0.5s"
            style={{ width: "500px" }}
          />
        </DesktopRow>

        <DesktopRow delay="0.4s">
          <ImageWindow
            src={img2}
            type="circle"
            delay="0.7s"
            style={{ width: "150px" }}
          />
          <Text>Your</Text>
          <Text className="emphasis">Beauty.</Text>
        </DesktopRow>

        <DesktopRow delay="0.6s">
          <Text className="emphasis">Defined.</Text>
          <ImageWindow
            src={img3}
            type="pill"
            delay="0.9s"
            style={{ width: "500px" }}
          />
          <Text className="emphasis">Timeless!</Text>
        </DesktopRow>

        {/* Mobile Layout */}
        <MobileColumn>
          <MobileRow delay="0.2s">
            <Text className="emphasis">Discover</Text>
          </MobileRow>

          <MobileImageRow delay="0.3s">
            <ImageWindow
              src={img1}
              type="pill"
              delay="0.5s"
              style={{ width: "250px" }}
            />
          </MobileImageRow>

          <MobileRow delay="0.4s">
            <Text>Your</Text>
            <ImageWindow
              src={img2}
              type="circle"
              delay="0.7s"
              style={{ width: "80px" }}
            />
          </MobileRow>

          <MobileRow delay="0.5s">
            <Text className="emphasis">Beauty.</Text>
            <Text className="emphasis">Defined.</Text>
          </MobileRow>

          <MobileImageRow delay="0.6s">
            <ImageWindow
              src={img3}
              type="pill"
              delay="0.9s"
              style={{ width: "250px" }}
            />
          </MobileImageRow>

          <MobileRow delay="0.7s">
            <Text className="emphasis">Timeless!</Text>
          </MobileRow>
        </MobileColumn>

        <Subtitle>
          Beauty designed to make you feel effortlessly confident
        </Subtitle>

        <CTAButton
          ref={buttonRef}
          shouldAnimate={shouldAnimateButton}
          onClick={handleButtonClick}
        >
          Start Your Journey
        </CTAButton>
      </ContentContainer>
    </HeroSection>
  );
};

export default Banner;
